import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { Plus, Users, Pencil, Trash2, ArrowRight } from 'lucide-react';
import { api } from '../lib/api';
import PageHeader from '../components/PageHeader';
import EmptyState from '../components/EmptyState';
import PhoneInput from '../components/PhoneInput';
import DocumentInput, { formatDocBR } from '../components/DocumentInput';
import { formatPhoneBR } from '../components/PhoneInput';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Card, CardContent } from '../components/ui/card';
import { Skeleton } from '../components/ui/skeleton';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';

export default function Partners() {
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [show, setShow] = useState(false);
  const [form, setForm] = useState({
    name: '',
    email: '',
    phone: '',
    document: '',
    notes: '',
    default_contribution_pct: '',
    default_profit_pct: '',
  });

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get('/partners');
      setItems(data);
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const openCreate = () => {
    setEditing(null);
    setForm({ name: '', email: '', phone: '', document: '', notes: '', default_contribution_pct: '', default_profit_pct: '' });
    setShow(true);
  };
  const openEdit = (p) => {
    setEditing(p);
    setForm({
      name: p.name || '',
      email: p.email || '',
      phone: p.phone || '',
      document: p.document || '',
      notes: p.notes || '',
      default_contribution_pct: p.default_contribution_pct ?? '',
      default_profit_pct: p.default_profit_pct ?? '',
    });
    setShow(true);
  };

  const submit = async () => {
    if (!form.name.trim()) {
      toast.error('Nome é obrigatório');
      return;
    }
    try {
      const payload = {
        name: form.name.trim(),
        email: form.email.trim() || null,
        phone: form.phone || null,
        document: form.document || null,
        notes: form.notes.trim() || null,
        default_contribution_pct: form.default_contribution_pct !== '' && form.default_contribution_pct !== null ? Number(form.default_contribution_pct) : null,
        default_profit_pct: form.default_profit_pct !== '' && form.default_profit_pct !== null ? Number(form.default_profit_pct) : null,
      };
      if (editing) {
        await api.put(`/partners/${editing.id}`, payload);
      } else {
        await api.post('/partners', payload);
      }
      toast.success(editing ? 'Sócio atualizado' : 'Sócio criado');
      setShow(false);
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Erro ao salvar');
    }
  };

  const remove = async (p) => {
    if (!window.confirm(`Excluir sócio ${p.name}?`)) return;
    try {
      await api.delete(`/partners/${p.id}`);
      toast.success('Sócio excluído');
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Não foi possível excluir');
    }
  };

  return (
    <>
      <PageHeader
        title="Sócios"
        subtitle="Cadastre os sócios investidores e acompanhe o extrato consolidado de cada um."
        actions={
          <Button onClick={openCreate} className="bg-[hsl(var(--primary))] hover:bg-[hsl(var(--primary))]/90" data-testid="partners-new-button">
            <Plus className="h-4 w-4 mr-1.5" /> Novo sócio
          </Button>
        }
      />

      {loading ? (
        <div className="space-y-3">
          {Array.from({ length: 3 }).map((_, i) => <Skeleton key={i} className="h-16 rounded-xl" />)}
        </div>
      ) : items.length === 0 ? (
        <EmptyState
          icon={Users}
          title="Nenhum sócio cadastrado"
          description="Cadastre os sócios investidores para poder definir a composição societária dos veículos."
          action={{ label: 'Cadastrar sócio', onClick: openCreate }}
        />
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
          {items.map((p) => (
            <Card key={p.id} className="border-border/70 hover:shadow-sm transition-shadow" data-testid="partner-card">
              <CardContent className="p-4">
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0">
                    <div className="font-semibold">{p.name}</div>
                    {p.email && <div className="text-xs text-muted-foreground truncate">{p.email}</div>}
                    {p.phone && <div className="text-xs text-muted-foreground">{formatPhoneBR(p.phone)}</div>}
                    {p.document && <div className="text-xs text-muted-foreground">Doc: {formatDocBR(p.document)}</div>}
                  </div>
                  <div className="flex flex-col gap-1">
                    <Button variant="ghost" size="icon" onClick={() => openEdit(p)} data-testid="partner-edit">
                      <Pencil className="h-3.5 w-3.5" />
                    </Button>
                    <Button variant="ghost" size="icon" onClick={() => remove(p)} data-testid="partner-delete">
                      <Trash2 className="h-3.5 w-3.5 text-[hsl(var(--danger))]" />
                    </Button>
                  </div>
                </div>
                <Button
                  variant="outline"
                  size="sm"
                  className="mt-3 w-full"
                  onClick={() => navigate(`/socios/${p.id}`)}
                  data-testid="partner-view-statement"
                >
                  Ver extrato <ArrowRight className="h-3.5 w-3.5 ml-1" />
                </Button>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      <Dialog open={show} onOpenChange={setShow}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{editing ? 'Editar sócio' : 'Novo sócio'}</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label>Nome *</Label>
              <Input value={form.name} onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))} data-testid="partner-name-input" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>E-mail</Label>
                <Input type="email" value={form.email} onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))} data-testid="partner-email-input" />
              </div>
              <div>
                <Label>Telefone</Label>
                <PhoneInput value={form.phone} onChange={(v) => setForm((f) => ({ ...f, phone: v }))} data-testid="partner-phone-input" />
              </div>
            </div>
            <div>
              <Label>Documento (CPF/CNPJ)</Label>
              <DocumentInput value={form.document} onChange={(v) => setForm((f) => ({ ...f, document: v }))} data-testid="partner-document-input" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>% Aporte padrão</Label>
                <Input
                  type="number"
                  step="0.01"
                  min="0"
                  max="100"
                  value={form.default_contribution_pct}
                  onChange={(e) => setForm((f) => ({ ...f, default_contribution_pct: e.target.value }))}
                  placeholder="Ex: 50"
                  className="text-right tabular"
                  data-testid="partner-default-contribution-input"
                />
                <p className="text-[10px] text-muted-foreground mt-1">% de capital padrão deste sócio em novos veículos.</p>
              </div>
              <div>
                <Label>% Resultado padrão</Label>
                <Input
                  type="number"
                  step="0.01"
                  min="0"
                  max="100"
                  value={form.default_profit_pct}
                  onChange={(e) => setForm((f) => ({ ...f, default_profit_pct: e.target.value }))}
                  placeholder="Ex: 30"
                  className="text-right tabular"
                  data-testid="partner-default-profit-input"
                />
                <p className="text-[10px] text-muted-foreground mt-1">% de lucro padrão deste sócio.</p>
              </div>
            </div>
            <div>
              <Label>Observações</Label>
              <Textarea rows={2} value={form.notes} onChange={(e) => setForm((f) => ({ ...f, notes: e.target.value }))} data-testid="partner-notes-input" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShow(false)}>Cancelar</Button>
            <Button onClick={submit} className="bg-[hsl(var(--primary))] hover:bg-[hsl(var(--primary))]/90" data-testid="partner-save-button">Salvar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
